import { createEffect, createMemo, createSignal, on, onCleanup, onMount, Show } from "solid-js";
import { Outlet, useLocation, useNavigate, useRouterState } from "@tanstack/solid-router";
import type { AbstractPowerSyncDatabase } from "@powersync/common";

import {
  getInspectionStatusCountsQuery,
  getOpenFindingCountsQuery,
  queryKeys,
} from "@fieldcheck/shared";
import { createPowerSyncQuery } from "@fieldcheck/shared/solid";

import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { useActiveContext } from "@/features/organizations/hooks/use-active-context";
import { appState, Skeleton, useInteractionFlag } from "@fleet/ui";
import { useTheme } from "@/providers/theme-provider";
import { useCommandHandler } from "@fleet/shortcuts";
import { COMMAND_IDS } from "@/lib/shortcuts/defaults";

import { AppBackground } from "@/components/app-background";
import { AppHeader } from "@/components/app-header";
import { OfflineBanner } from "@/components/offline-banner";
import { ShortcutsModal } from "@/components/shortcuts-modal";
import { AccountSwitcherHost } from "@/features/auth/account-switcher-host";
import { AppLayoutSidebar } from "./app-layout-sidebar";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import type { SidebarStats } from "@/components/app-sidebar";
import { AppErrorBoundary } from "@/components/ui/error-boundary";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { useUndo } from "@/lib/undo";

interface AppLayoutContentProps {
  db: AbstractPowerSyncDatabase | null;
}

/**
 * Granular skeleton for the main content region, shown while PowerSync's local
 * database is still initializing (`props.db` is null). Mirrors the coarse shape
 * of a typical list/detail screen so the shell never collapses to blank space.
 */
function ContentSkeleton() {
  return (
    <div class="flex flex-1 flex-col gap-6 p-4 sm:p-6" aria-hidden="true">
      {/* Page header */}
      <div class="flex items-center justify-between gap-4">
        <div class="flex flex-col gap-2">
          <Skeleton class="h-7 w-48" />
          <Skeleton class="h-4 w-64" />
        </div>
        <Skeleton class="h-9 w-28" />
      </div>

      {/* Summary cards */}
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Skeleton class="h-24 w-full" />
        <Skeleton class="h-24 w-full" />
        <Skeleton class="h-24 w-full" />
        <Skeleton class="h-24 w-full" />
      </div>

      {/* List rows */}
      <div class="flex flex-col gap-3">
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
        <Skeleton class="h-16 w-full" />
      </div>
    </div>
  );
}

function useSidebarEscape(isSidebarOpen: () => boolean, setIsSidebarOpen: (o: boolean) => void) {
  onMount(() => {
    const handleEscapeForSidebar = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isSidebarOpen()) {
        setIsSidebarOpen(false);
      }
    };
    globalThis.addEventListener("keydown", handleEscapeForSidebar);
    onCleanup(() => {
      globalThis.removeEventListener("keydown", handleEscapeForSidebar);
    });
  });
}

export function AppLayoutContent(props: AppLayoutContentProps) {
  onMount(() => {
    // Notify the InitialSyncOverlay that we are mounted and ready
    appState.markAsReady();
  });

  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const routerState = useRouterState();
  const isNavigating = () => routerState().status === "pending";

  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;
  const theme = useTheme();

  const [isSidebarOpen, setIsSidebarOpen] = createSignal(false);
  const [shortcutsOpen, setShortcutsOpen] = createSignal(false);

  // Closes the mobile drawer on navigate.
  createEffect(() => {
    void location().pathname;
    setIsSidebarOpen(false);
  });

  useSidebarEscape(isSidebarOpen, setIsSidebarOpen);

  // With the nav drawer open, the horizontal gesture belongs to it (drag to
  // dismiss). Any future page-level swipe would listen on the window and fire
  // underneath it, so ownership is announced in the interaction context rather
  // than re-derived from the DOM by each gesture.
  useInteractionFlag({ "nav-drawer.open": isSidebarOpen });

  // Section key for the per-route entrance animation (slide-up + fade on mount).
  // Keying on the pathname remounts the wrapper around <Outlet> on navigation,
  // replaying the GPU-only `slide-in-up` keyframe (transform + opacity). The
  // inspection-run carousel pages section state internally (no URL change), so
  // paging through sections does NOT re-trigger the page entrance.
  // `prefers-reduced-motion` is honored globally in global.css.
  const routeSectionKey = createMemo(() => location().pathname || "/");

  // ── Undo L1 (ACTIVITY-CORE): Ctrl/Cmd+Z + pila por cuenta ────────────────
  const { undoLast, clearUndoStack } = useUndo();
  useCommandHandler(COMMAND_IDS.APP_UNDO, () => {
    void undoLast();
  });
  // The undo stack belongs to the ACTIVE account: emptied on account switch
  // (defer: don't clear on initial mount).
  createEffect(
    on(
      userId,
      () => {
        clearUndoStack();
      },
      { defer: true },
    ),
  );

  // ── Shortcuts globales (subset del sistema heredado) ─────────────────────
  useCommandHandler(COMMAND_IDS.HELP_OPEN, () => {
    setShortcutsOpen(true);
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_SIDEBAR, () => {
    setIsSidebarOpen((prev) => !prev);
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BLUR, () => {
    void theme.toggleBlur();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_FOLLOW_DEVICE_THEME, () => {
    void theme.toggleFollowDevice();
  });
  useCommandHandler(COMMAND_IDS.UI_TOGGLE_BACKGROUND_STYLE, () => {
    theme.toggleBackgroundStyle();
  });
  useCommandHandler(COMMAND_IDS.GOTO_DASHBOARD, () => {
    void navigate({ to: "/" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_PROFILE, () => {
    void navigate({ to: "/profile" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_THEMES, () => {
    void navigate({ to: "/themes" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_SHORTCUTS, () => {
    void navigate({ to: "/profile/shortcuts" });
  });
  useCommandHandler(COMMAND_IDS.GOTO_LANGUAGE, () => {
    void navigate({ to: "/language" });
  });

  // ── Sidebar badges: open findings + in-progress inspections ──────────────
  // Scoped to the ACTIVE org context so the badge matches the view
  // (same scope as the list queries; MULTI_ORG_SWITCHER guide).
  const activeCtx = useActiveContext();
  const openFindings = createPowerSyncQuery<{ severity: string | null; count: number }>({
    queryKey: () => queryKeys.findings.openStats(userId(), activeCtx.activeOrgId()),
    db: () => props.db,
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance
        ? getOpenFindingCountsQuery(dbInstance, { scope: activeCtx.scope() })
        : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    suspense: false,
  });

  const inspectionCounts = createPowerSyncQuery<{ status: string | null; count: number }>({
    queryKey: () => queryKeys.inspections.stats(userId(), activeCtx.activeOrgId()),
    db: () => props.db,
    queryFn: () => {
      const dbInstance = drizzle();
      return dbInstance
        ? getInspectionStatusCountsQuery(dbInstance, { scope: activeCtx.scope() })
        : null;
    },
    enabled: () => Boolean(drizzle()),
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
    suspense: false,
  });

  const stats = (): SidebarStats | undefined => {
    if (openFindings.isLoading || inspectionCounts.isLoading) {
      return undefined;
    }
    let open = 0;
    for (const row of openFindings.data ?? []) {
      open += row.count;
    }
    const inProgress =
      (inspectionCounts.data ?? []).find((r) => r.status === "in_progress")?.count ?? 0;
    return { openFindings: open, inProgress };
  };

  return (
    <AppBackground
      class="flex h-dvh text-foreground overflow-hidden"
      isBlur={theme.isBlur()}
      patternType={theme.tokens()?.patternType}
      patternOpacity={theme.tokens()?.patternOpacity}
    >
      {/* Skip-to-content: first focusable element, visually hidden until focused. */}
      <a
        href="#main-content"
        class="sr-only rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-lg focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[200]"
      >
        {t(msg`Skip to content`)}
      </a>

      <AppLayoutSidebar isOpen={isSidebarOpen} setOpen={setIsSidebarOpen} stats={stats} />

      {/* ── Main content ───────────────────────────────────────── */}
      <main
        id="main-content"
        tabindex="-1"
        class="flex flex-1 flex-col overflow-hidden bg-transparent pt-[var(--safe-area-inset-top)] pr-[var(--safe-area-inset-right)] pb-[var(--safe-area-inset-bottom)] outline-none"
      >
        <AppHeader onMenuClick={() => setIsSidebarOpen(true)} isNavigating={isNavigating} />
        <OfflineBanner />
        <div class="flex flex-1 flex-col min-h-0 overflow-hidden pr-[var(--safe-area-inset-right)] pl-[var(--safe-area-inset-left)]">
          <div class="flex flex-1 flex-col min-h-0 h-full">
            {/* Route content is data-bound: hold a granular skeleton until the
                local database is ready, then mount the real <Outlet />. A region
                ErrorBoundary keeps a thrown render error scoped to the content
                area — the shell (sidebar/header) stays interactive. */}
            <Show when={props.db} fallback={<ContentSkeleton />}>
              <AppErrorBoundary region={t(msg`this view`)}>
                {/* Per-route entrance: keying on the pathname remounts this
                    wrapper on navigation, replaying `slide-in-up` (GPU-only:
                    transform + opacity). Internal carousel/section state does
                    not change the key, so it won't re-trigger. Reduced-motion
                    is honored globally in global.css. */}
                <Show when={routeSectionKey()} keyed>
                  <div class="flex flex-1 flex-col min-h-0 h-full animate-slide-in-up will-change-[transform,opacity]">
                    <Outlet />
                  </div>
                </Show>
              </AppErrorBoundary>
            </Show>
          </div>
        </div>
      </main>

      <ShortcutsModal
        isOpen={shortcutsOpen()}
        onClose={() => {
          setShortcutsOpen(false);
        }}
      />

      {/* Multi-account switcher: mounted ONCE at layout level, URL-driven
          (`?account=switch`). The sidebar trigger only flips the URL flag. */}
      <AccountSwitcherHost />
    </AppBackground>
  );
}
